// Import global styles
import './src/styles/global.css';

const HASH_SCROLL_ATTEMPTS = 20;

function scrollToPageTop() {
    const html = document.documentElement;
    const body = document.body;
    const prevHtml = html.style.scrollBehavior;
    const prevBody = body.style.scrollBehavior;
    html.style.scrollBehavior = 'auto';
    body.style.scrollBehavior = 'auto';
    window.scrollTo(0, 0);
    html.scrollTop = 0;
    body.scrollTop = 0;
    document.getElementById('___gatsby')?.scrollTo?.(0, 0);
    document.getElementById('gatsby-focus-wrapper')?.scrollTo?.(0, 0);
    html.style.scrollBehavior = prevHtml;
    body.style.scrollBehavior = prevBody;
}

function scrollToLocationHash(hash) {
    if (!hash || hash === '#') return false;
    let id = hash.slice(1);
    try {
        id = decodeURIComponent(id);
    } catch {
        /* keep raw id */
    }
    // Skip target sits at the top of <main>. scrollIntoView on that node
    // (or on a tall focused landmark) lands mid-page because of smooth
    // scroll + scroll-padding. Pin skip to the viewport top.
    if (id === 'main-content') {
        scrollToPageTop();
        return true;
    }
    const el = document.getElementById(id);
    if (!el) return false;
    el.scrollIntoView({ block: 'start', behavior: 'smooth' });
    return true;
}

function scheduleHashScroll(hash) {
    let attempts = 0;
    const tick = () => {
        if (scrollToLocationHash(hash)) return;
        attempts += 1;
        if (attempts < HASH_SCROLL_ATTEMPTS) {
            requestAnimationFrame(tick);
        }
    };
    requestAnimationFrame(tick);
}

// Fixed header sits over the viewport top. Hash targets use scroll-margin /
// scroll-padding, and this waits until the element exists after client nav.
export const shouldUpdateScroll = ({ routerProps: { location } }) => {
    if (location.hash) {
        scheduleHashScroll(location.hash);
        return false;
    }
    return true;
};

// `gatsby-plugin-google-tagmanager` used to push a `gatsby-route-change` event
// to dataLayer on every client-side navigation (Gatsby is an SPA, so GTM's
// native History Change trigger doesn't see these). Now that GTM loads via
// Partytown (see gatsby-ssr.js) instead of that plugin, replicate the same
// event manually so existing GTM triggers keep firing on route changes.
export const onRouteUpdate = ({ location }) => {
    if (location.hash) {
        scheduleHashScroll(location.hash);
    }
    if (process.env.NODE_ENV === 'production') {
        // Delay slightly so document.title / SEO tags have updated first.
        setTimeout(() => {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({ event: 'gatsby-route-change' });
        }, 50);
    }
};

// `gatsby-plugin-google-tagmanager`'s `enableWebVitalsTracking` used to push
// Core Web Vitals to dataLayer for GTM/GA reporting via an old, transitive
// `web-vitals@1` import. Restored here as a direct dependency (latest
// `web-vitals`), using the same dataLayer event shape
// (`core-web-vitals` / `webVitalsMeasurement: { name, id, value }`) so any
// existing GTM tags/triggers built around that event keep working unchanged.
//
// Note: `FID` is deprecated upstream and replaced with `INP` — the old
// plugin tracked CLS/FID/LCP, this tracks CLS/INP/LCP. Also, rather than the
// old code's debounce-and-send-once-per-metric hack, this uses web-vitals'
// modern default behavior of reporting each metric once it's finalized
// (typically on visibility/unload), which is simpler and more accurate.
export const onInitialClientRender = () => {
    if (process.env.NODE_ENV !== 'production') {
        return;
    }

    import('web-vitals').then(({ onCLS, onINP, onLCP }) => {
        const sendToGTM = ({ name, value, id }) => {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                event: 'core-web-vitals',
                webVitalsMeasurement: {
                    name,
                    id,
                    // Google Analytics metrics must be integers; CLS is
                    // multiplied by 1000 first for precision (matches the
                    // convention the previous implementation used).
                    value: Math.round(name === 'CLS' ? value * 1000 : value),
                },
            });
        };

        onCLS(sendToGTM);
        onINP(sendToGTM);
        onLCP(sendToGTM);
    });
};
